import { useEffect, useRef, useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router";
import { classNames } from "../../ui/styles/classNames";
import AppButton from "../../ui/buttons/AppButton";
import { navigateWithPageTransition, PAGE_TRANSITION_DURATION_MS } from "./pageTransition";

interface AuthPageSwitcherProps {
  to: string;
  direction: "left" | "right";
  label: string;
  className?: string;
  compact?: boolean;
}

export default function AuthPageSwitcher({
  to,
  direction,
  label,
  className,
  compact = false,
}: AuthPageSwitcherProps) {
  const navigate = useNavigate();
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<number | null>(null);
  const resetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const handleSwitch = () => {
    if (animating) {
      return;
    }
    setAnimating(true);
    timerRef.current = navigateWithPageTransition((target) => void navigate(target), to, direction);
    resetTimerRef.current = window.setTimeout(() => {
      setAnimating(false);
    }, PAGE_TRANSITION_DURATION_MS + 80);
  };

  return (
    <div className={classNames("app-page-switcher", className)}>
      <AppButton
        onClick={handleSwitch}
        className={classNames("app-page-switcher-button", compact && "app-auth-switcher-compact")}
        variant="secondary"
        size={compact ? "xs" : "sm"}
        aria-label={label}
        disabled={animating}
      >
        {direction === "left" ? <ArrowLeftIcon className="h-4 w-4" /> : null}
        <span>{label}</span>
        {direction === "right" ? <ArrowRightIcon className="h-4 w-4" /> : null}
      </AppButton>
    </div>
  );
}
